import React, { useEffect, ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from './redux/store';
import { initializeAuthFromRefresh } from './redux/actions/auth';
import { fetchChannelsForOrg } from './redux/actions/channels';
import { fetchOrganizations } from './redux/actions/organizations';
import {
  fetchCurrentSubscription,
  fetchOrganizationUsage,
} from './redux/actions/subscription';
import {
  fetchChannelAbilities,
  fetchChannelRevisionAbilities,
  fetchOrganizationAbilities,
} from './redux/actions/abilities';
import { Spinner } from 'flowbite-react';
import { paths } from './routes/paths';
import { useRouteRequiresChannel } from './hooks/useRouteLayoutOptions';

function ProtectedRoute({
  children,
  isOrganizationCheckRequired = true,
  isSubscriptionCheckRequired = true,
}: {
  children: ReactElement;
  isOrganizationCheckRequired?: boolean;
  isSubscriptionCheckRequired?: boolean;
}): JSX.Element {
  // Whether the underlying route needs at least one channel in the active
  // workspace. Declared via `handle: { requiresChannel: true }` on the
  // route. Controls: (a) the fallback channel-fetch effect below, and
  // (b) whether channel-loading blocks render with a spinner.
  const requiresChannel = useRouteRequiresChannel();
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, user, isUserLoading, isInitializing } = useSelector(
    (state: RootState) => state.user
  );
  const {
    isChannelLoading,
    fetchedByOrg,
    activeChannelId,
    abilityRulesFetchedByChannel,
  } = useSelector((state: RootState) => state.channels);
  const {
    organizations,
    activeOrganizationId,
    isOrganizationLoading,
    hasFetchedOrganizations,
    abilityRulesFetchedByOrg,
  } = useSelector((state: RootState) => state.organizations);
  const {
    isSubscriptionLoading,
    fetchedByOrg: subscriptionFetchedByOrg,
    isUsageLoading,
    usageFetchedByOrg,
  } = useSelector((state: RootState) => state.subscription);

  // 1) Initialize auth on mount - only if still initializing
  // This handles: login → fetchOrganizations → fetchChannelsForOrg
  useEffect(() => {
    if (isInitializing) {
      dispatch(initializeAuthFromRefresh());
    }
  }, [isInitializing, dispatch]);

  // 2) Fetch organizations if authenticated and not fetched yet
  // NOTE: This is a fallback - normally auth flow handles this
  useEffect(() => {
    if (
      isOrganizationCheckRequired &&
      isAuthenticated &&
      !hasFetchedOrganizations &&
      !isOrganizationLoading
    ) {
      dispatch(fetchOrganizations());
    }
  }, [
    isOrganizationCheckRequired,
    isAuthenticated,
    hasFetchedOrganizations,
    isOrganizationLoading,
    dispatch,
  ]);

  // Gate org-scoped fetches on the active org being ACCEPTED. The backend
  // returns 401 for PENDING/REJECTED members on /channels, /abilities,
  // /usage, /subscription — without this gate the effects below loop because
  // the rejected reducers don't always set the fetched flag.
  const activeOrg = activeOrganizationId
    ? organizations[activeOrganizationId.toString()]
    : undefined;
  const isActiveOrgAccepted = activeOrg?.invitation_status === 'ACCEPTED';

  // 2.5) Fetch subscription for active organization if needed
  // NOTE: This is a fallback - normally auth flow handles this
  useEffect(() => {
    if (
      isSubscriptionCheckRequired &&
      isAuthenticated &&
      activeOrganizationId &&
      isActiveOrgAccepted &&
      !subscriptionFetchedByOrg[String(activeOrganizationId)] &&
      !isSubscriptionLoading
    ) {
      dispatch(fetchCurrentSubscription(activeOrganizationId));
    }
  }, [
    isSubscriptionCheckRequired,
    isAuthenticated,
    activeOrganizationId,
    isActiveOrgAccepted,
    subscriptionFetchedByOrg,
    isSubscriptionLoading,
    dispatch,
  ]);

  // 2.6) Fetch organization usage so limit-gate UI (channel + member
  // touchpoints) can render at-limit state on first visit without each
  // page re-fetching. Same cache pattern as subscription above.
  useEffect(() => {
    if (
      isAuthenticated &&
      activeOrganizationId &&
      isActiveOrgAccepted &&
      !usageFetchedByOrg[String(activeOrganizationId)] &&
      !isUsageLoading
    ) {
      dispatch(fetchOrganizationUsage(activeOrganizationId));
    }
  }, [
    isAuthenticated,
    activeOrganizationId,
    isActiveOrgAccepted,
    usageFetchedByOrg,
    isUsageLoading,
    dispatch,
  ]);

  // 3) Fetch channels for active organization if needed
  // NOTE: This is a fallback - normally auth flow handles this
  useEffect(() => {
    if (
      requiresChannel &&
      isAuthenticated &&
      activeOrganizationId &&
      isActiveOrgAccepted &&
      !fetchedByOrg[activeOrganizationId] &&
      !isChannelLoading
    ) {
      dispatch(fetchChannelsForOrg(activeOrganizationId));
    }
  }, [
    requiresChannel,
    isAuthenticated,
    activeOrganizationId,
    isActiveOrgAccepted,
    fetchedByOrg,
    isChannelLoading,
    dispatch,
  ]);

  // 4) Fetch CASL ability rules for the active organization, once per org.
  // Guard mirrors the channels/subscription pattern: skip when the org's
  // rules have already landed. Logout clears the flag (see reducer), so a
  // re-login refetches.
  useEffect(() => {
    if (
      isAuthenticated &&
      activeOrganizationId &&
      isActiveOrgAccepted &&
      !abilityRulesFetchedByOrg[activeOrganizationId]
    ) {
      dispatch(fetchOrganizationAbilities(activeOrganizationId));
    }
  }, [
    isAuthenticated,
    activeOrganizationId,
    isActiveOrgAccepted,
    abilityRulesFetchedByOrg,
    dispatch,
  ]);

  // 5) Fetch CASL ability rules for the active channel (channel + revision
  // scope, dispatched together — the flag is set when the channel-scope
  // response lands and gates both on subsequent navigations).
  useEffect(() => {
    if (
      isAuthenticated &&
      activeChannelId &&
      !abilityRulesFetchedByChannel[activeChannelId]
    ) {
      dispatch(fetchChannelAbilities(activeChannelId));
      dispatch(fetchChannelRevisionAbilities(activeChannelId));
    }
  }, [
    isAuthenticated,
    activeChannelId,
    abilityRulesFetchedByChannel,
    dispatch,
  ]);

  // Show loading during initialization or user/org/subscription/channel fetch
  if (
    isInitializing ||
    (isUserLoading && !user) ||
    (isOrganizationCheckRequired && isOrganizationLoading) ||
    (isSubscriptionCheckRequired && isSubscriptionLoading) ||
    (requiresChannel && isChannelLoading)
  ) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Spinner aria-label="Loading" size="xl" />
      </div>
    );
  }

  // Not authenticated after initialization - redirect to login
  if (!isAuthenticated) {
    return <Navigate to={paths.login} replace />;
  }

  // Redirect to organization creation if no organizations (after fetch completes)
  if (
    isOrganizationCheckRequired &&
    hasFetchedOrganizations &&
    Object.keys(organizations).length === 0
  ) {
    return <Navigate to={paths.createOrganization} replace />;
  }

  // If the user has memberships but none of them are ACCEPTED yet, force them
  // to the accept-invitation screen rather than into the dashboard chrome
  // (which would just 401 on every org-scoped fetch). This fires on ALL routes
  // — not gated on `requiresChannel` — so a user whose only workspace is a
  // pending invite can't slip into a half-broken dashboard via a settings/
  // non-channel route with no way to accept. The accept route lives outside
  // ProtectedRoute, so this can't loop. (A user with at least one ACCEPTED org
  // is not redirected; their pending invites are surfaced in the workspace
  // switcher instead.)
  if (
    isOrganizationCheckRequired &&
    hasFetchedOrganizations &&
    Object.keys(organizations).length > 0
  ) {
    const orgList = Object.values(organizations);
    const hasAccepted = orgList.some((o) => o.invitation_status === 'ACCEPTED');
    if (!hasAccepted) {
      const pending = orgList
        .filter((o) => o.invitation_status === 'PENDING')
        .sort((a, b) => a.organization_id - b.organization_id)[0];
      if (pending) {
        return (
          <Navigate
            to={paths.organizationAccept(String(pending.organization_id))}
            replace
          />
        );
      }
    }
  }

  // Note: when a channel-gated route is hit on an org with zero channels,
  // we no longer redirect to /onboarding. DashBoardLayout renders an
  // empty state in the content slot instead, so the sidebar/header (and
  // workspace switcher) stay reachable. The empty state copy + CTA are
  // gated on `orgAbility.can('create', 'channel')`.

  return children;
}

export default ProtectedRoute;
